import { useModalInstance } from '@core/components/ModalStack';
import { Theme } from '@core/utils/css';
import { PHONE_MEDIA_QUERY } from '@core/utils/hooks/dom';
import { R } from '@core/utils/r';
import { ClassNames, css, Global } from '@emotion/react';
import { Dialog, DialogProps } from '@mui/material';

import React, { CSSProperties, Fragment, memo, PropsWithChildren, useEffect, useMemo } from 'react';

const styles = {
  global: css`
    .MuiPopover-root {
      .MuiMenuItem-root {
        font-size: 15px;
        font-family: ${Theme.fontFamily};
      }
    }
  `,

  container: css`
    &,
    .MuiButton-label,
    .MuiTab-wrapper {
      font-family: ${Theme.fontFamily};
    }
  `,
};

export interface ModalProps {
  backdropClose?: boolean;
  fullWidth?: DialogProps['fullWidth'];
  maxWidth?: DialogProps['maxWidth'] | number | (string & {});
  scroll?: DialogProps['scroll'];
  borderRadius?: number;
  className?: string;
  hideBackdrop?: boolean;
  disableBackdropClick?: boolean;
  disableEnforceFocus?: boolean;
  disableScrollLock?: boolean;
  paperStyle?: CSSProperties;
  onClose?(): void | boolean;
  fullScreen?: boolean;
}

export const Modal = memo((props: PropsWithChildren<ModalProps>) => {
  const modal = useModalInstance();

  const maxWidth = useMemo(() => {
    let maxWidth: string | undefined = undefined;

    if (!props.maxWidth) {
      maxWidth = '790px';
    } else if (R.isNumber(props.maxWidth)) {
      maxWidth = props.maxWidth + 'px';
    } else if (R.isString(props.maxWidth) && (props.maxWidth.endsWith('px') || props.maxWidth.endsWith('%'))) {
      maxWidth = props.maxWidth;
    } else if (props.maxWidth === 'xl') {
      maxWidth = '100%';
    }

    return maxWidth;
  }, [props.maxWidth]);

  useEffect(() => {
    if (props.disableScrollLock) {
      document.body.style.overflow = 'auto'; // Enable scrolling when hideBackdrop is true
    } else {
      document.body.style.overflow = ''; // Let MUI handle scroll locking
    }

    return () => {
      document.body.style.overflow = ''; // Cleanup on unmount or close
    };
  }, [props.disableScrollLock]);

  return (
    <Fragment>
      <Global styles={styles.global} />

      <ClassNames>
        {({ css }) => (
          <Dialog
            componentsProps={{ backdrop: { style: { backgroundColor: Theme.isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)' } } }}
            open
            //onClose={props.backdropClose === false ? undefined : modal.close}
            onClose={
              props.backdropClose === false
                ? undefined
                : () => {
                    const result = props.onClose && props.onClose();
                    if (R.isBoolean(result) && result === false) {
                      return;
                    }

                    modal.close();
                  }
            }
            fullScreen={props.fullScreen}
            fullWidth={props.fullWidth !== false}
            maxWidth={maxWidth ? false : (props.maxWidth as any)}
            scroll={props.scroll}
            classes={{
              container: css(styles.container),
              paper: css({
                maxWidth: `${maxWidth} !important`,
                borderRadius: props.borderRadius && `${props.borderRadius}px !important`,
                border: Theme.isDark ? '1px solid #2a2a2a' : undefined,
                backgroundColor: Theme.isDark ? 'transparent' : undefined,
                ...(props.paperStyle || ({} as any)),
                ...(props.fullScreen
                  ? {}
                  : {
                      [`@media ${PHONE_MEDIA_QUERY}`]: {
                        margin: '8px !important',
                        width: 'calc(100% - 16px) !important',
                        maxHeight: 'calc(100% - 16px) !important',
                      },
                    }),
              }),
            }}
            className={props.className}
            hideBackdrop={props.hideBackdrop}
            disablePortal
            disableAutoFocus
            disableRestoreFocus
            disableEnforceFocus
          >
            {props.children}
          </Dialog>
        )}
      </ClassNames>
    </Fragment>
  );
});
